import { useMemo, useState } from "react";
import { RollbackOutlined } from "@ant-design/icons";
import { Button, Input, Modal, notification, Typography } from "antd";
import "./side-button-list.scss";
import { Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useTitleSectionsStore, type SectionId } from "@/features/journal-title-sections";
import { useValidateTitle } from "@/features/journal-title-validation";
import {
  type AmendmentAction,
  type AmendmentRequest,
  useAmendmentsStore,
} from "@/features/journal-version-list";
import { useDisclosure, useNavigationPosition } from "@/shared/lib/hooks";
import { isSuperAdminFromStorage } from "@/shared/lib/session-service";
import { SideButtonConfig } from "../../model";
import { useConfirmReviewerApproveModal } from "../../model/hooks/user-hooks/use-confirm-reviewer-aprove";
import { useRejectGSN } from "../../model/hooks/user-hooks/use-reject-gsn";
import { useRejectTitleModal } from "../../model/hooks/user-hooks/use-reject-title-modal";
import { useReturnTitleModal } from "../../model/hooks/user-hooks/use-return-title-modal";
import { useRevokeTitleModal } from "../../model/hooks/user-hooks/use-revoke-title-modal";
import { useSaveJournal } from "../../model/hooks/user-hooks/use-save-journal";
import { useSendApproveModal } from "../../model/hooks/user-hooks/use-send-approval-modal";
import { useSendToGSN } from "../../model/hooks/user-hooks/use-send-to-gsn";

type ModalAction =
  | "send_to_review"
  | "save"
  | "sign_changes"
  | "sign_authorized"
  | "return_to_work"
  | "withdraw"
  | "reject"
  | null;

const ACTION_CONFIG: Record<
  NonNullable<ModalAction>,
  {
    title: string;
    description: string;
    okText: string;
    okDanger?: boolean;
    hasComment?: boolean;
    commentLabel?: string;
    commentPlaceholder?: string;
  }
> = {
  send_to_review: {
    title: "Отправить на проверку",
    description: "Вы уверены, что хотите отправить заявку на проверку?",
    okText: "Отправить",
  },
  save: {
    title: "Сохранить заявку",
    description: "Вы уверены, что хотите сохранить изменения?",
    okText: "Сохранить",
  },
  sign_changes: {
    title: "Подписать заявку",
    description: "Вы уверены, что хотите подписать заявку на изменение?",
    okText: "Подписать",
  },
  sign_authorized: {
    title: "Подписать изменения",
    description: "Вы уверены, что хотите подписать изменения титульного листа?",
    okText: "Подписать",
  },
  return_to_work: {
    title: "Вернуть в работу",
    description: "Заявка будет возвращена в статус «Новое». Продолжить?",
    okText: "Вернуть",
  },
  withdraw: {
    title: "Отозвать заявку",
    description: "Вы уверены, что хотите отозвать заявку? Укажите причину отзыва.",
    okText: "Отозвать",
    okDanger: true,
    hasComment: true,
    commentLabel: "Причина отзыва",
    commentPlaceholder: "Укажите причину отзыва...",
  },
  reject: {
    title: "Отклонить заявку",
    description: "Вы уверены, что хотите отклонить заявку? Укажите причину отклонения.",
    okText: "Отклонить",
    okDanger: true,
    hasComment: true,
    commentLabel: "Причина отклонения",
    commentPlaceholder: "Укажите причину отклонения...",
  },
};

interface AmendmentButtonsProps {
  request: AmendmentRequest | null;
}

const AmendmentButtons = ({ request }: AmendmentButtonsProps) => {
  const [activeAction, setActiveAction] = useState<ModalAction>(null);
  const [comment, setComment] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const applyAction = useAmendmentsStore((state) => state.applyAction);

  const isSystemRequest = request?.requestSource === "SYSTEM";
  const isSigner = request?.viewerRole === "SIGNER";
  const cfg = activeAction
    ? {
      ...ACTION_CONFIG[activeAction],
      ...(isSystemRequest && activeAction === "send_to_review"
        ? {
          title: "Принять изменения",
          description:
              "Сохранить заполненные данные и передать изменения ответственному на подписание?",
          okText: "Принять",
        }
        : {}),
      ...(isSystemRequest && activeAction === "withdraw"
        ? {
          description:
              "Отозвать заявку с подписания и вернуть её в статус «Новая»?",
          hasComment: false,
          okDanger: false,
        }
        : {}),
    }
    : null;

  const openModal = (action: ModalAction) => {
    setComment("");
    setActiveAction(action);
  };

  const closeModal = () => {
    if (isProcessing) return;
    setActiveAction(null);
    setComment("");
  };

  const handleConfirm = async () => {
    if (!request || !activeAction) return;

    const actionMap: Record<NonNullable<ModalAction>, AmendmentAction> = {
      send_to_review: "SUBMIT",
      save: "SAVE",
      sign_changes: "SIGN_CHANGES",
      sign_authorized: "SIGN_AUTHORIZED",
      return_to_work: "RETURN_TO_WORK",
      withdraw: "WITHDRAW",
      reject: "REJECT",
    };

    setIsProcessing(true);
    const result = applyAction(request.key, actionMap[activeAction], {
      comment,
    });

    if (!result.ok) {
      if (
        result.error === "VALIDATION_ERROR" &&
        result.validationErrors?.length
      ) {
        notification.error({
          message: "Заполните обязательные поля",
          description: result.validationErrors
            .map((error) => error.message)
            .join(". "),
        });
      } else {
        notification.error({
          message: "Действие недоступно для текущего статуса заявки",
        });
      }
      setIsProcessing(false);
      return;
    }

    if (activeAction === "sign_changes" && !isSystemRequest) {
      await new Promise((resolve) => {
        setTimeout(resolve, 500);
      });
      applyAction(
        request.key,
        request.authorizedSignaturesRequired > 0
          ? "XML_READY_WITH_SIGNERS"
          : "XML_READY_WITHOUT_SIGNERS",
      );
    }

    notification.success({
      message:
        activeAction === "save"
          ? "Заявка сохранена"
          : "Статус заявки обновлён",
    });
    setIsProcessing(false);
    closeModal();
  };

  const status = request?.status;

  return (
    <>
      <div className="side-button-list__amendment-actions">
        {!request?.isSuperseded &&
        isSystemRequest &&
        status === "NEW" ? (
          <>
            <Button type="primary" block onClick={() => openModal("send_to_review")}>
              Принять
            </Button>
            <Button block onClick={() => openModal("reject")}>
              Отклонить
            </Button>
          </>
          ) : null}

        {!isSystemRequest && (!status || status === "NEW") ? (
          <>
            <Button type="primary" block onClick={() => openModal("send_to_review")}>
              Отправить заявку
            </Button>
            <Button block onClick={() => openModal("save")}>
              Сохранить
            </Button>
          </>
        ) : null}

        {!request?.isSuperseded &&
        isSystemRequest &&
        status === "SIGNING_CHANGES" &&
        isSigner ? (
          <>
            <Button type="primary" block onClick={() => openModal("sign_changes")}>
              Подписать
            </Button>
            <Button danger block onClick={() => openModal("reject")}>
              Отклонить
            </Button>
          </>
          ) : null}

        {!request?.isSuperseded &&
        isSystemRequest &&
        status === "SIGNING_CHANGES" &&
        !isSigner ? (
          <Button block onClick={() => openModal("withdraw")}>
            Отозвать заявку
          </Button>
          ) : null}

        {!isSystemRequest && status === "SIGNING_CHANGES" ? (
          <>
            <Button type="primary" block onClick={() => openModal("sign_changes")}>
              Подписать
            </Button>
            <Button danger block onClick={() => openModal("reject")}>
              Отклонить
            </Button>
            <Button danger block onClick={() => openModal("withdraw")}>
              Отозвать
            </Button>
          </>
        ) : null}

        {status === "PREPARING_XML" && (
          <Button disabled block loading>
            Подготовка XML
          </Button>
        )}

        {status === "ON_SIGNING" && (
          <>
            <Button
              type="primary"
              block
              onClick={() => openModal("sign_authorized")}
            >
              Подписать
            </Button>
            <Button danger block onClick={() => openModal("reject")}>
              Отклонить
            </Button>
          </>
        )}

        {status === "REJECTED" && !request?.isSuperseded ? (
          <Button
            type="primary"
            block
            icon={<RollbackOutlined />}
            onClick={() => openModal("return_to_work")}
          >
            Вернуть в работу
          </Button>
        ) : null}

      </div>

      <Modal
        open={!!activeAction}
        title={cfg?.title}
        onCancel={closeModal}
        closable={!isProcessing}
        keyboard={!isProcessing}
        maskClosable={!isProcessing}
        destroyOnClose
        footer={
          <div className="side-button-list__modal-footer">
            <Button onClick={closeModal} disabled={isProcessing}>
              Отменить
            </Button>
            <Button
              type="primary"
              danger={cfg?.okDanger}
              onClick={handleConfirm}
              loading={isProcessing}
              disabled={cfg?.hasComment && !comment.trim()}
            >
              {cfg?.okText}
            </Button>
          </div>
        }
      >
        <div className="side-button-list__modal-body">
          <Typography.Text>{cfg?.description}</Typography.Text>
          {cfg?.hasComment && (
            <div>
              <Typography.Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                {cfg.commentLabel}
              </Typography.Text>
              <Input.TextArea
                rows={4}
                placeholder={cfg.commentPlaceholder}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export const SideButtonList = ({ control }: any) => {
  const { isTitleAmendmentsForm } = useNavigationPosition();
  const { requestId } = useParams<{ requestId?: string }>();
  const amendmentRequests = useAmendmentsStore((s) => s.requests);
  const currentAmendment = requestId
    ? amendmentRequests.find((r) => r.key === requestId)
    : null;

  const [isLoading, setIsLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isSuperAdmin = useMemo(() => isSuperAdminFromStorage(), []);

  const [status, setStatus] = useState<string>("");
  const [modalContentNew, setModalContentNew] = useState({
    okTest: "Сохранить",
    cancelText: "Отменить",
    title: "Подтвердить сохранение",
    content: "Вы уверены, что хотите сохранить изменения?",
    isEditTest: false,
  });

  const handleClear = () => {
    setModalContentNew({
      okTest: "",
      cancelText: "",
      title: "",
      content: "",
      isEditTest: false,
    });
    onClose();
    setStatus("");
  };

  const { handleSave } = useSaveJournal({ handleClear });

  const { handleSendApproval } = useSendApproveModal({
    handleClear,
    setIsLoading,
  });

  const { handleRevoke } = useRevokeTitleModal({
    handleClear,
    setIsLoading,
  });
  const { handleConfirmReviewerApprove } = useConfirmReviewerApproveModal({
    handleClear,
    setIsLoading,
  });

  const { handleReject } = useRejectTitleModal({
    handleClear,
    setIsLoading,
  });
  const { handleResend: handleReturnToWork } = useReturnTitleModal({
    handleClear,
    setIsLoading,
  });
  const { handleResend } = useReturnTitleModal({
    handleClear,
    setIsLoading,
  });

  const handleSign = () => {};
  const handleDeclineSignature = () => {};
  const { handleSendToGSN } = useSendToGSN({ handleClear, setIsLoading });
  const { handleRejectGSN } = useRejectGSN({ handleClear, setIsLoading });

  const handleOk = () => {
    switch (status) {
      case "SAVE":
        handleSave();
        break;
      case "SEND_APPROVAL":
        handleSendApproval();
        break;
      case "REVOKE":
        handleRevoke();
        break;
      case "RETURN_TO_WORK":
        handleReturnToWork();
        break;
      case "SIGN":
        handleSign();
        break;
      case "DECLINE_SIGNATURE":
        handleDeclineSignature();
        break;
      case "SEND_TO_GSN":
        handleSendToGSN();
        break;
      case "APPROVE":
        handleConfirmReviewerApprove();
        break;
      case "REJECT":
        handleReject();
        break;
      case "RESEND":
        handleResend();
        break;
      case "PREPARE_FILES_RQ":
        handleConfirmReviewerApprove();
        break;
      case "REJECT_GSN":
        handleRejectGSN();
        break;
      default:
        break;
    }
  };
  const handleCancel = () => {
    setModalContentNew({
      okTest: "",
      cancelText: "",
      title: "",
      content: "",
      isEditTest: false,
    });
    onClose();
  };

  const { errorList } = useValidateTitle();
  const openSections = useTitleSectionsStore((state) => state.openSections);

  const handleCloseWithNotification = () => {
    notification.info({
      message: "Заполните недостающие данные",
      description: "Пожалуйста, заполните следующие поля:",
      placement: "topRight",
    });
    onClose();

    const errorToSection: Record<number, SectionId> = {
      1: "journals-info", 2: "journals-info", 3: "journals-info", 4: "journals-info",
      5: "developer-company", 6: "developer-company", 15: "developer-company", 24: "developer-company", 34: "developer-company",
      7: "tec-customer-company", 8: "tec-customer-company", 26: "tec-customer-company", 27: "tec-customer-company",
      9: "building-contractor-company", 10: "building-contractor-company", 25: "building-contractor-company",
      33: "documentation-contractor-company", 30: "documentation-contractor-company",
      16: "designer-supervisions", 18: "designer-supervisions", 31: "designer-supervisions",
      21: "other-developers", 32: "other-developers",
    };

    const sectionsToOpen = [...new Set(errorList.map((err) => errorToSection[err.id]).filter(Boolean))];
    openSections(sectionsToOpen);
  };

  if (isTitleAmendmentsForm) {
    return <AmendmentButtons request={currentAmendment ?? null} />;
  }

  return (
    <div className="side-button-list__root">
      <SideButtonConfig
        onOpen={onOpen}
        setModalContentNew={setModalContentNew}
        setStatus={setStatus}
      />
      {/* <Modal
        title={modalContent.title}
        key={modalContent.title}
        open={isOpen}
        okText="Сохранить"
        cancelText="Отменить"
        onOk={modalContent.onOk}
        onCancel={modalContent.onCancel}
        footer={
          <>
            <Button
              type="primary"
              loading={isLoading}
              // onClick={modalContent.onOk}
              onClick={() => modalContent.onOk()}
            >
              {modalContent.okTest}
            </Button>
            <Button onClick={modalContent.onCancel}>
              {modalContent.cancelText}
            </Button>
          </>
        }
      >
        <div className="side-button-list__content">{modalContent.content}</div>
        {modalContent.isEditTest && <Input.TextArea />}
      </Modal> */}
      <Modal
        title={
          errorList.length > 0 && status === "SEND_APPROVAL"
            ? "Недостаточно данных"
            : modalContentNew.title
        }
        key={modalContentNew.title}
        open={isOpen}
        okText="Сохранить"
        cancelText="Отменить"
        onOk={handleOk}
        onCancel={handleCancel}
        centered
        footer={
          <>
            {status === "SEND_APPROVAL" && errorList.length > 0 ? (
              <>
                <Button type="primary" onClick={handleCloseWithNotification}>
                  Заполнить
                </Button>
                {isSuperAdmin && (
                  <Button
                    color="danger"
                    variant="outlined"
                    onClick={() => handleOk()}
                  >
                    Понятно, проигнорировать (для тестов)
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button
                  type="primary"
                  loading={isLoading}
                  onClick={() => handleOk()}
                >
                  {modalContentNew.okTest}
                </Button>
                <Button onClick={handleCancel}>
                  {modalContentNew.cancelText}
                </Button>
              </>
            )}
          </>
        }
      >
        {status === "SEND_APPROVAL" && errorList.length > 0 && (
          <div className="issues-while-validation-wrapper">
            <div className="issues-while-validation-wrapper__content">
              <ol className="issues-while-validation-wrapper__list">
                {errorList.map((error) => (
                  <li
                    key={error.id}
                    className="issues-while-validation-wrapper__item"
                  >
                    {error.description}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}

        {(status !== "SEND_APPROVAL" && status !== "SEND_TO_GSN") ||
          (!errorList.length &&
            (status === "SEND_APPROVAL" || status === "SEND_TO_GSN") && (
              <div className="side-button-list__content">
                {modalContentNew.content}
              </div>
            ))}
        {((status !== "SEND_APPROVAL" && status !== "SEND_TO_GSN") ||
          (!errorList.length &&
            (status === "SEND_APPROVAL" || status === "SEND_TO_GSN"))) &&
          modalContentNew.isEditTest && (
            <Controller
              name="comment"
              control={control}
              render={({ field }) => (
                <Input.TextArea
                  {...field}
                  placeholder="Введите комментарий"
                  rows={4}
                />
              )}
            />
          )}
      </Modal>
    </div>
  );
};
